import * as dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { isSameData } from '../dto/data-model-utils.js';
import { OnlineEventTicketOrder } from './online-event-ticket-order.js';
import { EventTicketOrder } from './event-ticket-order.js';
import { BusinessModuleRef } from './business-module-ref.js';

export enum OrderCleanupRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED'
}

export class OrderCleanupRequest extends DataModel {
  static readonly TYPE_ID: string = '76265ded706f460599615a968f4bed39';

  private constructor(
    public readonly uniqueId: string,
    public readonly status: OrderCleanupRequestStatus,
    public readonly order: CustomerOrderBase,
    public readonly creationDate: dayjs.Dayjs,
    public readonly completionDate: dayjs.Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OrderCleanupRequest.TYPE_ID;
  }

  get creatorId(): string {
    return this.order.creatorId;
  }

  get userId(): string | null {
    return this.order.userId;
  }

  get isGuestOrder(): boolean {
    return this.order.isGuestOrder;
  }

  get businessRef(): BusinessModuleRef {
    return this.order.businessRef;
  }

  get eventId(): string | null {
    return (OnlineEventTicketOrder.assertType(this.order) || EventTicketOrder.assertType(this.order)) ?
        this.order.eventId : null;
  }

  static create(opts: {
      uniqueId: string,
      order: CustomerOrderBase,
      creationDate: dayjs.Dayjs,
  }): OrderCleanupRequest {
    return new OrderCleanupRequest(
        opts.uniqueId,
        OrderCleanupRequestStatus.Pending,
        opts.order,
        opts.creationDate,
        null,
    );
  }

  static fromOrder(order: CustomerOrderBase): OrderCleanupRequest | null {
    // there is no payment to fail so we don't have to worry about the order needing to be cleaned up
    if(order.onlinePaymentDetails == null) return null;

    return new OrderCleanupRequest(
        order.uniqueId,
        OrderCleanupRequestStatus.Pending,
        order.clone(),
        TimeZone.cloneDate(order.creationDate),
        null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderCleanupRequest {
    return new OrderCleanupRequest(
        MapUtils.get<string>(map, 'uniqueId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), OrderCleanupRequestStatus),
        context.fromData<CustomerOrderBase>(MapUtils.get<SerializedData>(map, 'order')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'completionDate')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.order.uniqueId,
      'creatorId': this.creatorId,
      'userId': this.userId,
      'eventId': this.eventId,
      'isGuestOrder': this.isGuestOrder,
      'order': context.toData(this.order),
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'completionDate': context.encodeDateOrNull(this.completionDate),
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new OrderCleanupRequest(
        this.uniqueId,
        this.status,
        this.order.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDateOrNull(this.completionDate),
    ) as this;
  }

  isSame(other: OrderCleanupRequest): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creationDate === this.creationDate &&
        other.completionDate === this.completionDate &&
        other.status === this.status &&
        isSameData(other.order, this.order);
  }

  copyAsComplete(completionDate: dayjs.Dayjs): this {
    return new OrderCleanupRequest(
        this.uniqueId,
        OrderCleanupRequestStatus.Completed,
        this.order.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(completionDate),
    ) as this;
  }
}

export class OrderCleanupRequestBuilder extends DataModelBuilder<OrderCleanupRequest> {
  build(context: SerializationContext, map: SerializedData): OrderCleanupRequest {
    return OrderCleanupRequest.fromMap(context, map);
  }
}
